import { Component, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { NgRedux } from '@angular-redux/store';
import { Subscription } from 'rxjs';
import { IAppState, AuthResponse, User } from '@core/redux/stores';
import {
  ModalService,
  AuthTrackingService,
  ProxyService,
  AuthenticationService,
  AppleRedirectResult,
  ValidationService,
  AlertService,
  CustomizeHttpheaderService,
  GtmService,
  AsyncValidatorWExt,
  CiamFlagsService,
} from '@core/services';
import { ApiRoutes } from '@shared/models/configs/api.endpoints';
import { RequestUser, LoginIntent } from '@features/authenticate/models';
import { ErrorCodes } from '@shared/models/error-codes/response.error-codes';
import { Constants } from '@core/constants/core.constants';
import { environment } from '@environments/environment';
import { ModalTrackingGtmDto, GtmExternal } from '@shared/models/configs/gtm.external';
import { SurveyService } from '@core/services/survey.service';
import { ConfirmModalService } from '@core/services/confirm-modal.service';
import { ErrorCodesService } from '@core/services/error-codes.service';
import { normalizePhoneForAuth0Sms } from '@shared/utils/phone-e164';
import { UpdateTrigger } from '@shared/components/validation-message';
import {
  applyAuth0OtpErrorUiState,
  resetAuth0OtpErrorUiState,
  resolveAuth0OtpHttpError,
} from '@features/authenticate/auth0-otp-error.util';

type Step =
  | 'email'
  | 'password'
  | 'otpConfirm'
  | 'otpVerify'
  | 'social'
  | 'facebookEmailCollection'
  | 'signupProfile'
  | 'signupPhoneOtp'
  | 'socialChallenge';
type AuthMethod = 'password' | 'otp' | 'social';

interface Auth0IdentifyResponse {
  authMethod: AuthMethod;
  channel?: string;
  maskedIdentifier?: string;
  providers?: string[];
}

const PROVIDER_LABELS: Record<string, string> = {
  'google-oauth2': 'Google',
  'apple': 'Apple',
  'facebook': 'Facebook',
  'facebookoauth2': 'Facebook',
};

const RESEND_COOLDOWN_SECONDS = 60;

@Component({
  selector: 'app-identifier-first-signin',
  templateUrl: './identifier-first-signin.component.html',
  styleUrls: ['../register/register.component.less', './identifier-first-signin.component.less'],
  providers: [AlertService],
})
export class IdentifierFirstSigninComponent implements OnInit, OnDestroy, AfterViewInit {
  step: Step = 'email';
  emailForm: UntypedFormGroup;
  passwordForm: UntypedFormGroup;
  otpForm: UntypedFormGroup;
  signupProfileForm: UntypedFormGroup;

  emailValue = '';
  phoneValue = '';
  authMethod: AuthMethod = 'password';
  channel = '';
  maskedIdentifier = '';
  socialProviders: string[] = [];
  identifierValue = '';

  loading = false;
  disableContinue = true;
  errorMessage = '';
  ciamV6Com = false;
  /** Top banner (e.g. 429 / rate limit from CX or Auth0) for OTP steps — Figma lockout / too-many-codes */
  otpRateLimitBanner = '';
  otpContinueDisabled = false;
  otpResendDisabled = false;
  otpShowContactUs = false;
  /** Signup Create Account only: SMS initiate failure shown under the phone field. */
  signupProfilePhoneSendError = '';
  /** Set after email OTP verify when the API returns a pending signup token (encrypted payload server-side). */
  pendingSignupToken = '';
  signupEmailOffersLabel = ['Send me emails about rewards and promotions'];
  updateTrigger = UpdateTrigger;
  disableSignupProfileContinue = true;
  /** E.164 from profile step when user adds phone (SMS OTP before T&amp;C). */
  signupPhoneE164 = '';
  signupPhoneMasked = '';
  returnUrl = '';
  /** Provider email for social new-user (S12): displayed read-only in signupProfile step. */
  socialEmail = '';
  /** Encrypted challenge token from 202 response (S14 Facebook). */
  pendingSocialChallengeToken = '';
  /** Masked email shown to the user during S14 challenge ("Enter the code we sent to j***@..."). */
  socialChallengeMaskedEmail = '';
  /** Full email used as identifier for sessions/otp/verify during S14 challenge. */
  socialChallengeEmail = '';
  /** Encrypted token from facebook_email_required (Facebook CTE with no email). */
  facebookBindingToken = '';
  facebookEmailCollected = '';
  facebookEmailBindingFlow = false;
  phoneCollisionMaskedEmail: string | null = null;
  resendCooldownSeconds = 0;

  readonly ciamResendOtpVerify = (): void => this.onResendOtp();
  readonly ciamResendSignupPhoneOtp = (): void => this.initiateSignupPhoneOtp();
  readonly ciamResendSocialChallengeOtp = (): void => this.onResendSocialChallengeOtp();

  private subscriptions = new Subscription();
  private resendTimer: ReturnType<typeof setInterval> | null = null;
  private transitioningToTerms = false;

  constructor(
    public alertService: AlertService,
    private fb: UntypedFormBuilder,
    private modalService: ModalService,
    private authTracking: AuthTrackingService,
    private proxyService: ProxyService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthenticationService,
    private customizeHeaderService: CustomizeHttpheaderService,
    private gtmService: GtmService,
    private confirmModalService: ConfirmModalService,
    private survey: SurveyService,
    private cdr: ChangeDetectorRef,
    private ciamFlagsService: CiamFlagsService
  ) {
    this.authService.setAlertService(this.alertService);
    this.emailForm = this.fb.group({
      email: new UntypedFormControl(null, [], [
        AsyncValidatorWExt.debounce(ValidationService.asyncEmailValidator, 800),
      ]),
      phone: new UntypedFormControl(null, [ValidationService.phoneValidator]),
    });
    this.passwordForm = this.fb.group({
      password: new UntypedFormControl(null, [Validators.required]),
    });
    this.otpForm = this.fb.group({
      otpCode: new UntypedFormControl('', [Validators.required, Validators.minLength(6)]),
    });
    this.signupProfileForm = this.fb.group({
      firstname: new UntypedFormControl(null, [Validators.required, ValidationService.firstNameLastNameValidator]),
      lastname: new UntypedFormControl(null, [Validators.required, ValidationService.firstNameLastNameValidator]),
      phone: new UntypedFormControl(null),
      addToEmail: new UntypedFormControl(null),
    });
  }

  ngOnInit(): void {
    this.ciamV6Com = this.ciamFlagsService.isCiamV6Com();
    this.subscriptions.add(
      this.emailForm.valueChanges.subscribe(() => this.updateContinueButton())
    );
    this.subscriptions.add(
      this.emailForm.statusChanges.subscribe(() => this.updateContinueButton())
    );
    this.subscriptions.add(
      this.activatedRoute.queryParams.subscribe((params) => {
        this.returnUrl = (params[Constants.RETURN_URL] as string) || '';
        if (this.returnUrl) this.returnUrl = decodeURIComponent(this.returnUrl);
      })
    );
    this.subscriptions.add(
      this.confirmModalService.btnEvent.subscribe((data: string) => {
        if (data === 'resetPWBtn') this.showModal('app-forgot-password');
      })
    );
    this.subscriptions.add(this.signupProfileForm.valueChanges.subscribe(() => this.updateSignupProfileContinue()));
    this.subscriptions.add(this.signupProfileForm.statusChanges.subscribe(() => this.updateSignupProfileContinue()));
  }

  /** Apple Sign In returns to `/home?applesigninorigin=...`; legacy sign-in handled this in `SigninModalComponent`. */
  async ngAfterViewInit(): Promise<void> {
    this.gtmService.sendPageName(GtmExternal.VALUE_SIGNIN);
    const snapshotParams = this.activatedRoute.snapshot.queryParams;
    if (!snapshotParams[Constants.APPLE_SIGNIN_ORIGIN]) {
      return;
    }
    const appleResult = await this.authService.handleAppleSignInRedirect(snapshotParams);
    this.applyAppleRedirectResult(appleResult);
    this.loading = false;
    this.disableContinue = false;
    this.cdr.detectChanges();
  }

  private applyAppleRedirectResult(appleResult: AppleRedirectResult): void {
    if (appleResult.status === 'social_challenge') {
      this.pendingSocialChallengeToken = appleResult.pendingSocialChallengeToken;
      this.socialChallengeMaskedEmail = appleResult.maskedEmail;
      this.socialChallengeEmail = this.resolveSocialChallengeEmail(appleResult.maskedEmail);
      this.setFacebookChallengeBinding(appleResult.facebookBindingToken);
      this.otpForm.reset();
      this.step = 'socialChallenge';
      this.onCiamOtpSent();
      return;
    }
    if (appleResult.status === 'success' && this.authService.getErrorMessage() === null) {
      this.onAuthSuccess();
      return;
    }
    if (appleResult.status !== 'terms_pending'
      && appleResult.status !== 'incomplete'
      && this.authService.getErrorMessage() !== null) {
      this.alertService.error(this.authService.getErrorMessage());
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.clearCiamResendCooldown();
    if (!this.authTracking.isLoggedIn() && !this.transitioningToTerms) {
      this.authService.clearGuestOrderContext();
    }
    if (!this.authTracking.isLoggedIn() && this.returnUrl) {
      const urlArray = this.router.url.split('?');
      this.router.navigate([urlArray[0]]);
    }
    const params = this.activatedRoute.snapshot.queryParams;
    if (
      params[Constants.APPLE_SIGNIN_ORIGIN] &&
      ((params[Constants.CANCELLED] || params[Constants.SUCCESS] === 'false') || this.authTracking.isLoggedIn())
    ) {
      this.authService.cleanUpAppleSigninQueryParams();
    }
  }

  onIdentifierEmailBlur(): void {
    this.emailForm.get('email')?.markAsTouched();
  }

  private queueAutoSendOtp(): void {
    if (!this.ciamV6Com) {
      return;
    }
    setTimeout(() => {
      if (this.step === 'otpConfirm' && this.channel === 'email') {
        this.onOtpSendCode();
      }
    }, 0);
  }

  private updateSignupProfileContinue(): void {
    const ok =
      this.signupProfileForm.valid &&
      !!this.signupProfileForm.get('firstname')?.value?.trim() &&
      !!this.signupProfileForm.get('lastname')?.value?.trim();
    this.disableSignupProfileContinue = !ok;
    this.cdr.markForCheck();
  }

  private updateContinueButton(): void {
    const emailCtrl = this.emailForm.get('email');
    const hasEmail = emailCtrl?.valid && emailCtrl?.value?.trim();
    this.disableContinue = !hasEmail;
    this.cdr.markForCheck();
  }

  onEmailContinue(): void {
    this.errorMessage = '';
    const email = this.emailForm.get('email')?.value?.trim() || '';

    if (!email) return;

    this.emailValue = email;
    this.loading = true;
    this.disableContinue = true;
    this.gtmService.sendPageName(GtmExternal.VALUE_SIGNIN);

    this.proxyService.httpPost(ApiRoutes.AUTH0_IDENTIFY, { email }).subscribe(
      (resp: any) => {
        const res = (resp?.body ?? resp) as Auth0IdentifyResponse;
        this.loading = false;
        this.disableContinue = false;
        if (!res || !res.authMethod) {
          if (this.ciamV6Com) {
            this.errorMessage =
              'We could not complete sign-in for that email. Check the address or use Contact us for help.';
          } else {
            this.authMethod = 'password';
            this.step = 'password';
          }
          return;
        }
        this.authMethod = res.authMethod;
        if (res.authMethod === 'otp') {
          this.channel = res.channel ?? 'email';
          this.maskedIdentifier = res.maskedIdentifier ?? email;
          this.identifierValue = email;
          this.step = 'otpConfirm';
          if (this.ciamV6Com && (this.channel === 'email' || this.channel === '')) {
            this.queueAutoSendOtp();
          }
        } else if (res.authMethod === 'social') {
          this.socialProviders = res.providers ?? [];
          this.step = 'social';
        } else {
          this.step = 'password';
        }
      },
      () => {
        this.loading = false;
        this.disableContinue = false;
        this.errorMessage = 'We could not continue with that email. Please try again or visit /contact-us for help.';
        if (this.ciamV6Com) {
          this.errorMessage = 'We could not continue with that email. Please try again or use Contact us for help.';
        }
      }
    );
  }

  private maskPhoneLocal(phone: string): string {
    const d = String(phone).replace(/\D/g, '');
    if (d.length < 4) return '***';
    return `***-***-${d.slice(-4)}`;
  }

  /** Full identifier on CIAM OTP screens (no masking). */
  get ciamOtpDestination(): string {
    if (this.step === 'signupPhoneOtp') {
      const raw = (this.signupProfileForm?.get('phone')?.value ?? '').toString().trim();
      return this.formatUsPhoneForDisplay(raw || this.signupPhoneE164);
    }
    if (this.step === 'socialChallenge') {
      return (this.socialChallengeEmail || this.socialChallengeMaskedEmail || this.emailValue || '').trim();
    }
    if (this.channel === 'sms') {
      return this.formatUsPhoneForDisplay(this.identifierValue || this.emailValue || '');
    }
    return (this.identifierValue || this.emailValue || '').trim();
  }

  private formatUsPhoneForDisplay(phone: string): string {
    if (!phone) {
      return '';
    }
    const digits = phone.replace(/\D/g, '');
    const tenDigit = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    if (tenDigit.length === 10) {
      return `(${tenDigit.slice(0, 3)}) ${tenDigit.slice(3, 6)}-${tenDigit.slice(6)}`;
    }
    return phone.trim();
  }

  get ciamOtpHeading(): string {
    if (this.step === 'signupPhoneOtp' || this.channel === 'sms') {
      return 'Verify your phone number';
    }
    return 'Verify your email';
  }

  onPasswordSubmit(): void {
    this.errorMessage = '';
    const password = this.passwordForm.get('password')?.value;
    if (!password) return;

    this.loading = true;
    const userData: RequestUser = {
      email: this.emailValue,
      password,
      deviceDetails: null,
      intent: LoginIntent.Login,
    };

    this.proxyService
      .httpPost(ApiRoutes.LOGIN, userData, this.customizeHeaderService.addLongAndLat())
      .subscribe(
        (response) => {
          const signInResponse = response.body as AuthResponse;
          this.authService.setUserData(signInResponse);
          this.authService.sendGTMSignInEvent(signInResponse.user, false);
          this.onAuthSuccess();
          this.loading = false;
        },
        (err) => this.processPasswordErr(err)
      );
  }

  private processPasswordErr(err: any): void {
    if (err?.error?.code === ErrorCodes.INCORRECT_USERNAME_OR_PWD) {
      this.alertService.error(
        "Uh-oh, your login didn't work that time. Please check the email and password you entered."
      );
    } else if (err?.error?.code === ErrorCodes.ACCOUNT_LCK) {
      this.confirmModalService.show(
        'Account is now locked',
        '',
        'Due to 3 unsuccessful sign in attempts, this account is locked for 30 minutes. ' +
          'You may reset your password now or try again later.',
        'Reset Password',
        '',
        'Try Again Later',
        true,
        'resetPWBtn'
      );
    } else if (err?.error?.token) {
      this.authService.setMFAToken(err.error);
      this.authService.showNewDevicePopup(this.emailValue);
    } else if (err?.error?.code === 4037) {
      this.authService.showOKModal(err.error.code);
    } else {
      this.alertService.error(
        "Ooops, we're having a technical issue with our system. Please try again later."
      );
    }
    this.loading = false;
  }

  onOtpSendCode(): void {
    resetAuth0OtpErrorUiState(this);
    this.loading = true;
    let identifierForApi = this.identifierValue;
    if (this.channel === 'sms') {
      const normalized = normalizePhoneForAuth0Sms(this.identifierValue);
      if (!normalized) {
        this.loading = false;
        this.errorMessage = 'Enter a valid phone number (10 digits for US, or include country code).';
        return;
      }
      identifierForApi = normalized;
    }
    const payload: Record<string, string> = {
      identifier: identifierForApi,
      channel: this.channel,
      send: 'code',
    };
    this.proxyService.httpPost(ApiRoutes.AUTH0_OTP_INITIATE, payload).subscribe(
      () => {
        this.loading = false;
        this.step = 'otpVerify';
        this.otpForm.reset();
        this.onCiamOtpSent();
      },
      (err) => {
        this.loading = false;
        this.handleOtpHttpError(err, 'Failed to send code. Please try again.');
      }
    );
  }

  onOtpVerify(): void {
    resetAuth0OtpErrorUiState(this);
    const raw = this.otpForm.get('otpCode')?.value ?? '';
    const code = String(raw).replace(/\D/g, '').slice(0, 6);
    if (!code || code.length < 6) {
      this.errorMessage = 'Please enter the 6-digit code.';
      return;
    }
    this.loading = true;
    let identifierForApi = this.identifierValue;
    if (this.channel === 'sms') {
      const normalized = normalizePhoneForAuth0Sms(this.identifierValue);
      if (!normalized) {
        this.loading = false;
        this.errorMessage = 'Enter a valid phone number (10 digits for US, or include country code).';
        return;
      }
      identifierForApi = normalized;
    }
    const payload: Record<string, string> = {
      identifier: identifierForApi,
      otpCode: code,
      channel: this.channel,
    };
    const payloadWithOrder = this.authService.appendGuestOrderToPayload(payload);
    if (this.facebookEmailBindingFlow) {
      this.proxyService
        .httpPost(ApiRoutes.AUTH_EXTERNAL_FACEBOOK_BIND_EMAIL, {
          facebookBindingToken: this.facebookBindingToken,
          email: this.facebookEmailCollected || this.identifierValue,
          otpCode: code,
        })
        .subscribe(
          (response: any) => {
            this.loading = false;
            this.facebookEmailBindingFlow = false;
            const status = (response as any)?.status ?? 200;
            const body = (response as any)?.body ?? response;
            this.applySocialSignupExternalBody(body, status);
          },
          (err) => {
            this.loading = false;
            this.handleFacebookBindEmailError(err);
          }
        );
      return;
    }

    this.proxyService.httpPost(ApiRoutes.AUTH0_OTP_VERIFY, payloadWithOrder).subscribe(
      (response: any) => {
        const body = (response?.body ?? response) as Record<string, unknown>;
        if (this.ciamV6Com) {
          const pending = this.readPendingSignupToken(body);
          if (pending) {
            this.pendingSignupToken = pending;
            this.loading = false;
            this.step = 'signupProfile';
            this.signupProfileForm.reset();
            this.updateSignupProfileContinue();
            return;
          }
        }
        this.authService.setAuth0TokensFromOtpVerify(
          body as { accessToken?: string; refreshToken?: string; expiresIn?: number; tokenType?: string },
          () => {
            this.modalService.hideModal();
          }
        );
        this.loading = false;
        this.onAuthSuccess();
      },
      (err) => {
        this.loading = false;
        if (this.ciamV6Com && err?.error?.code === ErrorCodes.AUTH0_SIGNUP_NOT_ALLOWED) {
          this.otpRateLimitBanner = '';
          this.errorMessage = this.messageForAuth0OtpVerifyConflict(err);
          return;
        }
        this.handleOtpHttpError(err, 'Invalid code. Please try again.', {
          suppressAuth0SendFailureMessage: true,
        });
      }
    );
  }

  private readPendingSignupToken(body: Record<string, unknown>): string | undefined {
    const raw = body?.pendingSignupToken ?? body?.PendingSignupToken;
    return typeof raw === 'string' && raw.length > 0 ? raw : undefined;
  }

  onSignupProfileContinue(): void {
    this.errorMessage = '';
    this.signupProfilePhoneSendError = '';
    if (!this.signupProfileForm.valid || !this.pendingSignupToken) {
      return;
    }
    const v = this.signupProfileForm.value;
    const rawPhone = (v.phone ?? '').toString().trim();
    const phoneDigits = rawPhone ? rawPhone.replace(/\D/g, '') : '';
    if (phoneDigits && (phoneDigits.length !== 10 || phoneDigits === '0000000000')) {
      this.signupProfilePhoneSendError = 'Enter a valid 10-digit U.S. phone number or leave phone blank.';
      return;
    }
    if (phoneDigits) {
      const normalized = normalizePhoneForAuth0Sms(phoneDigits);
      if (!normalized) {
        this.signupProfilePhoneSendError = 'Enter a valid 10-digit U.S. phone number or leave phone blank.';
        return;
      }
      this.signupPhoneE164 = normalized;
      this.signupPhoneMasked = this.maskPhoneLocal(phoneDigits);
      this.otpForm.reset();
      this.postSignupPhoneOtpInitiate(true);
      return;
    }
    this.openTermsForSignup();
  }

  initiateSignupPhoneOtp(): void {
    if (this.loading || this.otpResendDisabled || this.resendCooldownSeconds > 0) {
      return;
    }
    this.postSignupPhoneOtpInitiate(false);
  }

  /** @param fromSignupProfileStep when true, stay on Create Account until send succeeds, then go to Verify. */
  private postSignupPhoneOtpInitiate(fromSignupProfileStep: boolean): void {
    resetAuth0OtpErrorUiState(this);
    this.signupProfilePhoneSendError = '';
    this.loading = true;
    this.proxyService
      .httpPost(ApiRoutes.AUTH0_OTP_INITIATE, {
        identifier: this.signupPhoneE164,
        channel: 'sms',
        send: 'code',
      })
      .subscribe(
        () => {
          this.loading = false;
          if (fromSignupProfileStep) {
            this.step = 'signupPhoneOtp';
          }
          this.onCiamOtpSent();
          this.cdr.markForCheck();
        },
        (err) => {
          this.loading = false;
          const code = err?.error?.code;
          if (
            !fromSignupProfileStep &&
            Number(code) === ErrorCodes.AUTH0_AUTHENTICATION_FAILED
          ) {
            this.step = 'signupProfile';
          }
          const sendToPhoneField =
            fromSignupProfileStep || this.step === 'signupProfile';
          this.handleOtpHttpError(err, 'Failed to send code. Please try again.', {
            signupProfilePhoneSendToField: sendToPhoneField,
          });
        }
      );
  }

  onSignupPhoneOtpVerify(): void {
    this.errorMessage = '';
    this.otpRateLimitBanner = '';
    const raw = this.otpForm.get('otpCode')?.value ?? '';
    const code = String(raw).replace(/\D/g, '').slice(0, 6);
    if (!code || code.length < 6) {
      this.errorMessage = 'Please enter the 6-digit code.';
      return;
    }
    this.loading = true;
    const payload: Record<string, string> = {
      identifier: this.signupPhoneE164,
      otpCode: code,
      channel: 'sms',
    };
    const payloadWithOrder = this.authService.appendGuestOrderToPayload(payload);
    this.proxyService.httpPost(ApiRoutes.AUTH0_OTP_VERIFY, payloadWithOrder).subscribe(
      (response: any) => {
        const body = (response?.body ?? response) as Record<string, unknown>;
        const idToken = this.readIdTokenFromOtpBody(body);
        if (!idToken) {
          this.loading = false;
          this.errorMessage = 'Unexpected response from server. Please try again.';
          return;
        }
        // In a signup context (pendingSignupToken present), always route through signup/link-phone.
        // The server resolves both cases: sms| = new phone (link it); email| = collision (already_phone).
        // Outside signup context, a non-sms| sub means Auth0 signed in an existing account via phone.
        const auth0Sub = ((body?.auth0Sub ?? body?.Auth0Sub) as string | undefined) ?? '';
        if (!this.pendingSignupToken && auth0Sub && !auth0Sub.startsWith('sms|')) {
          const existingAccessToken = (body?.accessToken ?? body?.AccessToken) as string | undefined;
          if (existingAccessToken?.trim()) {
            this.signupPhoneE164 = '';
            this.signupPhoneMasked = '';
            this.authService.setAuth0TokensFromOtpVerify(
              body as { accessToken?: string; refreshToken?: string; expiresIn?: number; tokenType?: string },
              () => { this.modalService.hideModal(); }
            );
            this.loading = false;
            this.onAuthSuccess();
            return;
          }
          this.loading = false;
          this.errorMessage = 'Unexpected response from server. Please try again.';
          return;
        }
        const phoneRaw = (this.signupProfileForm.get('phone')?.value ?? '').toString().trim();
        this.proxyService
          .httpPost(ApiRoutes.AUTH0_SIGNUP_LINK_PHONE, {
            pendingSignupToken: this.pendingSignupToken,
            smsIdToken: idToken,
            phone: phoneRaw || this.signupPhoneE164,
          })
          .subscribe(
            () => {
              this.loading = false;
              this.openTermsForSignup();
            },
            (err) => {
              this.loading = false;
              const reason = (err?.error?.reason ?? err?.error?.value?.reason ?? '').toString();
              const maskedEmail = (err?.error?.value?.maskedEmail ?? err?.error?.maskedEmail) as string | undefined;
              if (reason === 'already_phone') {
                this.phoneCollisionMaskedEmail = maskedEmail ?? null;
                this.signupProfilePhoneSendError = maskedEmail
                  ? `This phone is already linked to ${maskedEmail}. Use a different number or continue without one.`
                  : 'This phone number is already linked. Use a different number or continue without one.';
                this.errorMessage = '';
                this.step = 'signupProfile';
                this.otpForm.reset();
                return;
              }
              this.signupProfilePhoneSendError =
                err?.error?.message ?? 'Could not verify your phone. Please try again.';
              this.errorMessage = '';
            }
          );
      },
      (err) => {
        this.loading = false;
        this.handleOtpHttpError(err, 'Invalid code. Please try again.', {
          suppressAuth0SendFailureMessage: true,
        });
      }
    );
  }

  private readIdTokenFromOtpBody(body: Record<string, unknown>): string | undefined {
    const v = body?.idToken ?? body?.IdToken;
    return typeof v === 'string' && v.length > 0 ? v : undefined;
  }

  private openTermsForSignup(): void {
    const v = this.signupProfileForm.value;
    const rawPhone = (v.phone ?? '').toString().trim();
    const phoneDigits = rawPhone ? rawPhone.replace(/\D/g, '') : '';
    this.authService.prepareAuth0OtpSignupForTerms({
      pendingSignupToken: this.pendingSignupToken,
      firstName: v.firstname?.trim(),
      lastName: v.lastname?.trim(),
      phone: phoneDigits,
      zipCode: '',
      emailOptIn: !!v.addToEmail,
    });
    this.transitioningToTerms = true;
    this.modalService.setModalSelector('app-term-n-condition');
    this.modalService.showModal(false);
  }

  tryDifferentSignupMethod(): void {
    this.pendingSignupToken = '';
    this.socialEmail = '';
    this.pendingSocialChallengeToken = '';
    this.socialChallengeEmail = '';
    this.socialChallengeMaskedEmail = '';
    this.facebookBindingToken = '';
    this.facebookEmailBindingFlow = false;
    this.step = 'email';
    this.errorMessage = '';
    this.signupProfilePhoneSendError = '';
    this.signupPhoneE164 = '';
    this.signupPhoneMasked = '';
    this.otpForm.reset();
    this.signupProfileForm.reset();
    this.updateSignupProfileContinue();
  }

  /** Prefer API message; optional structured hint in error.value (camelCase or PascalCase). */
  private messageForAuth0OtpVerifyConflict(err: any): string {
    const m = err?.error?.message;
    if (typeof m === 'string' && m.trim()) {
      return m.trim();
    }
    const v = err?.error?.value ?? err?.error?.Value;
    const hint = (v?.signInHint ?? v?.SignInHint ?? '').toString().toLowerCase();
    if (hint === 'sign_in_social') {
      const providers = v?.providers ?? v?.Providers;
      const list = Array.isArray(providers)
        ? providers.map((p: string) => PROVIDER_LABELS[p] ?? p).filter(Boolean)
        : [];
      return list.length
        ? `An account already exists. Sign in with ${list.join(' or ')}.`
        : 'An account already exists. Sign in with your social provider.';
    }
    if (hint === 'sign_in_otp') {
      return 'An account already exists. Sign in with email or phone code instead.';
    }
    if (hint === 'sign_in_password') {
      return 'An account already exists. Sign in with your email and password.';
    }
    return 'An account already exists for this email. Try signing in instead.';
  }

  private handleOtpHttpError(
    err: any,
    fallbackMessage: string,
    opts?: {
      suppressAuth0SendFailureMessage?: boolean;
      /** When true, message goes under phone on Create Account instead of the form-level banner. */
      signupProfilePhoneSendToField?: boolean;
    }
  ): void {
    const code = err?.error?.code;
    if (
      opts?.suppressAuth0SendFailureMessage &&
      code !== undefined &&
      code !== null &&
      Number(code) === ErrorCodes.AUTH0_AUTHENTICATION_FAILED
    ) {
      this.signupProfilePhoneSendError = '';
      this.errorMessage = '';
      this.otpRateLimitBanner = '';
      resetAuth0OtpErrorUiState(this);
      return;
    }

    const state = resolveAuth0OtpHttpError(err, fallbackMessage);
    if (opts?.signupProfilePhoneSendToField && state.inlineMessage && !state.bannerMessage) {
      this.signupProfilePhoneSendError = state.inlineMessage;
      this.errorMessage = '';
      this.otpRateLimitBanner = '';
      this.otpShowContactUs = false;
      this.otpContinueDisabled = false;
      this.otpResendDisabled = false;
      return;
    }

    this.signupProfilePhoneSendError = '';
    applyAuth0OtpErrorUiState(state, this);
  }

  async onSocialClick(provider: string): Promise<void> {
    this.loading = true;
    this.alertService.clear();
    this.cdr.detectChanges();
    const p = (provider ?? '').toLowerCase();
    if (this.ciamV6Com && !p.includes('apple')) {
      await this.handleSocialCteClick(p);
      return;
    }
    if (p.includes('google')) {
      await this.authService.googleRegister(false, true);
      if (this.authService.getErrorMessage() === null) this.onAuthSuccess();
      else this.alertService.error(this.authService.getErrorMessage());
    } else if (p.includes('facebook')) {
      await this.authService.facebookRegister(false, true);
      if (this.authService.facebookProfile && this.authService.doesAccountPreexist) {
        await this.authService.completeRegistration();
        if (this.authService.getErrorMessage() === null) this.onAuthSuccess();
        else this.alertService.error(this.authService.getErrorMessage());
      } else if (this.authService.getErrorMessage()) {
        this.alertService.error(this.authService.getErrorMessage());
      }
    } else if (p.includes('apple')) {
      this.authService.appleAuth('signIn');
    } else {
      await this.authService.googleRegister(false, true);
      if (this.authService.getErrorMessage() === null) this.onAuthSuccess();
      else this.alertService.error(this.authService.getErrorMessage());
    }
    this.loading = false;
    this.cdr.detectChanges();
  }

  private async handleSocialCteClick(p: string): Promise<void> {
    if (p.includes('google')) {
      await this.authService.googleRegister(false, true);
    } else if (p.includes('facebook')) {
      await this.authService.facebookRegister(false, true);
    } else {
      this.loading = false;
      return;
    }

    if (this.authService.getErrorMessage()) {
      this.alertService.error(this.authService.getErrorMessage());
      this.loading = false;
      this.cdr.detectChanges();
      return;
    }

    try {
      const result = await this.authService.socialCteAsync();

      if (result.outcome === 'signed_in') {
        this.loading = false;
        this.cdr.detectChanges();
        this.onAuthSuccess();
        return;
      }

      if (result.outcome === 'new_user') {
        this.pendingSignupToken = result.pendingSignupToken;
        this.socialEmail = result.email ?? '';
        this.signupProfileForm.patchValue({
          firstname: result.firstName ?? '',
          lastname: result.lastName ?? '',
        });
        this.updateSignupProfileContinue();
        this.loading = false;
        this.step = 'signupProfile';
        this.cdr.detectChanges();
        return;
      }

      if (result.outcome === 'challenge') {
        this.pendingSocialChallengeToken = result.pendingSocialChallengeToken;
        this.socialChallengeMaskedEmail = result.maskedEmail;
        this.socialChallengeEmail = this.resolveSocialChallengeEmail(result.maskedEmail, result.email);
        this.setFacebookChallengeBinding(result.facebookBindingToken);
        this.otpForm.reset();
        this.loading = false;
        if (this.shouldCollectFacebookEmailInsteadOfChallenge(result.maskedEmail, this.socialChallengeEmail)) {
          this.facebookEmailCollected = '';
          this.facebookEmailBindingFlow = false;
          this.errorMessage = '';
          this.emailForm.patchValue({ email: '' });
          this.updateContinueButton();
          this.step = 'facebookEmailCollection';
        } else {
          this.step = 'socialChallenge';
          this.onCiamOtpSent();
        }
        this.cdr.detectChanges();
        return;
      }
    } catch (err: any) {
      const reason = (err?.error?.reason ?? (err?.error?.value?.reason ?? '')).toString();
      const errorCode = err?.error?.code as number | undefined;
      const isFacebookEmailRequired =
        reason === 'facebook_email_required' || errorCode === ErrorCodes.FACEBOOK_EMAIL_REQUIRED;
      if (isFacebookEmailRequired) {
        const token =
          (err?.error?.value?.facebookBindingToken ?? err?.error?.facebookBindingToken ?? '') as string;
        this.facebookBindingToken = token;
        this.facebookEmailCollected = '';
        this.facebookEmailBindingFlow = false;
        this.errorMessage = '';
        this.emailForm.patchValue({ email: '' });
        this.updateContinueButton();
        this.step = 'facebookEmailCollection';
        this.cdr.detectChanges();
        return;
      }
      if (reason === 'already_phone') {
        this.errorMessage =
          'This phone number is already linked to another account. Use a different number or proceed without one.';
      } else {
        this.alertService.error(err?.error?.message ?? 'An error occurred. Please try again.');
      }
    }
    this.loading = false;
    this.cdr.detectChanges();
  }

  onFacebookEmailContinue(): void {
    this.errorMessage = '';
    const email = this.emailForm.get('email')?.value?.trim() || '';
    if (!email || this.emailForm.get('email')?.invalid) {
      this.errorMessage = 'Enter a valid email address.';
      return;
    }
    this.facebookEmailCollected = email;
    this.loading = true;
    this.proxyService
      .httpPost(ApiRoutes.AUTH0_OTP_INITIATE, {
        identifier: email,
        channel: 'email',
        send: 'code',
      })
      .subscribe(
        () => {
          this.facebookEmailBindingFlow = true;
          this.channel = 'email';
          this.identifierValue = email;
          this.maskedIdentifier = this.maskEmailForDisplay(email);
          this.otpForm.reset();
          this.loading = false;
          this.step = 'otpVerify';
          this.onCiamOtpSent();
          this.cdr.detectChanges();
        },
        (err) => {
          this.loading = false;
          this.handleOtpHttpError(err, 'Failed to send code. Please try again.');
        }
      );
  }

  private applySocialSignupExternalBody(body: any, status: number): void {
    if (status === 202 || body?.challengeRequired) {
      this.pendingSocialChallengeToken = body?.pendingSocialChallengeToken ?? '';
      this.socialChallengeMaskedEmail = body?.maskedEmail ?? '';
      this.socialChallengeEmail = this.resolveSocialChallengeEmail(
        this.socialChallengeMaskedEmail,
        (body?.email ?? '').toString()
      );
      const fbToken = (body?.facebookBindingToken ?? '') as string;
      this.setFacebookChallengeBinding(fbToken);
      this.otpForm.reset();
      if (this.shouldCollectFacebookEmailInsteadOfChallenge(this.socialChallengeMaskedEmail, this.socialChallengeEmail)) {
        this.facebookEmailCollected = '';
        this.facebookEmailBindingFlow = false;
        this.errorMessage = '';
        this.emailForm.patchValue({ email: '' });
        this.updateContinueButton();
        this.step = 'facebookEmailCollection';
      } else {
        this.step = 'socialChallenge';
        this.onCiamOtpSent();
      }
      this.cdr.detectChanges();
      return;
    }

    if (body?.pendingSignupToken) {
      this.pendingSignupToken = body.pendingSignupToken as string;
      this.socialEmail = this.facebookEmailCollected;
      this.signupProfileForm.patchValue({
        firstname: body?.user?.firstName ?? body?.user?.FirstName ?? '',
        lastname: body?.user?.lastName ?? body?.user?.LastName ?? '',
      });
      this.updateSignupProfileContinue();
      this.step = 'signupProfile';
      this.cdr.detectChanges();
      return;
    }

    if (body?.tokenInfo && body?.user) {
      this.authService.applySocialSignupSignInResponse(body);
      this.onAuthSuccess();
      return;
    }

    this.errorMessage = 'Unexpected response from server. Please try again.';
    this.cdr.detectChanges();
  }

  private handleFacebookBindEmailError(err: any): void {
    const reason = (err?.error?.reason ?? err?.error?.value?.reason ?? '').toString();
    if (reason === 'already_phone') {
      this.errorMessage =
        'This phone number is already linked to another account. Use a different number or proceed without one.';
      return;
    }
    if (err?.status === 202 || err?.error?.challengeRequired) {
      const body = err?.error ?? err;
      this.applySocialSignupExternalBody(body, 202);
      return;
    }
    this.handleOtpHttpError(err, 'Invalid code. Please try again.', {
      suppressAuth0SendFailureMessage: true,
    });
  }

  private handleFacebookBindEmailSuccess(body: Record<string, unknown>): void {
    this.facebookEmailBindingFlow = false;
    this.facebookBindingToken = '';
    const status = 200;
    const bodyAny = body as any;
    if (bodyAny?.pendingSignupToken) {
      this.pendingSignupToken = bodyAny.pendingSignupToken as string;
      this.socialEmail = this.facebookEmailCollected || this.socialChallengeEmail;
      this.signupProfileForm.patchValue({
        firstname: bodyAny?.user?.firstName ?? bodyAny?.user?.FirstName ?? '',
        lastname: bodyAny?.user?.lastName ?? bodyAny?.user?.LastName ?? '',
      });
      this.updateSignupProfileContinue();
      this.step = 'signupProfile';
      this.cdr.detectChanges();
      return;
    }
    if (bodyAny?.tokenInfo && bodyAny?.user) {
      this.authService.applySocialSignupSignInResponse(body);
      this.onAuthSuccess();
      return;
    }
    this.applySocialSignupExternalBody(body, status);
  }

  private maskEmailForDisplay(email: string): string {
    const at = email.indexOf('@');
    if (at <= 1) return email;
    return email[0] + '***' + email.substring(at);
  }

  onSocialChallengeVerify(): void {
    this.errorMessage = '';
    this.otpRateLimitBanner = '';
    const raw = this.otpForm.get('otpCode')?.value ?? '';
    const code = String(raw).replace(/\D/g, '').slice(0, 6);
    if (!code || code.length < 6) {
      this.errorMessage = 'Please enter the 6-digit code.';
      return;
    }
    this.loading = true;

    // Path B (new FB user with email): verify OTP via facebook-bind-email, not social-link-challenge-complete.
    if (this.facebookBindingToken?.trim()) {
      this.proxyService
        .httpPost(ApiRoutes.AUTH_EXTERNAL_FACEBOOK_BIND_EMAIL, {
          facebookBindingToken: this.facebookBindingToken,
          email: this.facebookEmailCollected || this.socialChallengeEmail,
          otpCode: code,
        })
        .subscribe(
          (response: any) => {
            this.loading = false;
            const body = (response?.body ?? response) as Record<string, unknown>;
            this.handleFacebookBindEmailSuccess(body);
          },
          (err) => {
            this.loading = false;
            this.handleFacebookBindEmailError(err);
          }
        );
      return;
    }

    this.proxyService
      .httpPost(ApiRoutes.AUTH0_OTP_VERIFY, {
        identifier: this.socialChallengeEmail,
        otpCode: code,
        channel: 'email',
      })
      .subscribe(
        (response: any) => {
          const body = (response?.body ?? response) as Record<string, unknown>;
          const emailAccessToken = (body?.accessToken ?? body?.AccessToken) as string | undefined;
          const emailIdToken = (body?.idToken ?? body?.IdToken) as string | undefined;
          if (!emailAccessToken || !emailIdToken) {
            this.loading = false;
            this.errorMessage = 'Unexpected response from server. Please try again.';
            return;
          }
          this.proxyService
            .httpPost(ApiRoutes.AUTH0_SOCIAL_LINK_CHALLENGE_COMPLETE, {
              pendingSocialChallengeToken: this.pendingSocialChallengeToken,
              emailAccessToken,
              emailIdToken,
            })
            .subscribe(
              (r2: any) => {
                const b2 = (r2?.body ?? r2) as Record<string, unknown>;
                this.authService.setAuth0TokensFromOtpVerify(
                  b2 as { accessToken?: string; refreshToken?: string; expiresIn?: number; tokenType?: string },
                  () => { this.modalService.hideModal(); }
                );
                this.loading = false;
                this.onAuthSuccess();
              },
              (err) => {
                this.loading = false;
                this.handleOtpHttpError(err, 'Could not verify your email. Please try again.');
              }
            );
        },
        (err) => {
          this.loading = false;
          this.handleOtpHttpError(err, 'Invalid code. Please try again.', {
            suppressAuth0SendFailureMessage: true,
          });
        }
      );
  }

  onResendSocialChallengeOtp(): void {
    if (this.loading || this.otpResendDisabled || this.resendCooldownSeconds > 0) {
      return;
    }
    resetAuth0OtpErrorUiState(this);
    if (!this.socialChallengeEmail) return;
    this.loading = true;
    this.proxyService
      .httpPost(ApiRoutes.AUTH0_OTP_INITIATE, {
        identifier: this.socialChallengeEmail,
        channel: 'email',
        send: 'code',
      })
      .subscribe(
        () => {
          this.loading = false;
          this.onCiamOtpSent();
          this.cdr.markForCheck();
        },
        (err) => {
          this.loading = false;
          this.handleOtpHttpError(err, 'Failed to resend code. Please try again.');
        }
      );
  }

  onAuthSuccess(): void {
    this.authService.clearGuestOrderContext();
    this.survey.isQualtricsLoaded = false;
    this.modalService.hideModal();
    if (this.authTracking.isLoggedIn() && this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
    }
  }

  onBack(): void {
    if (this.step === 'signupPhoneOtp') {
      this.clearCiamResendCooldown();
      this.step = 'signupProfile';
      resetAuth0OtpErrorUiState(this);
      this.signupProfilePhoneSendError = '';
      this.otpForm.reset();
      return;
    }
    if (this.step === 'signupProfile') {
      this.errorMessage = '';
      this.signupProfilePhoneSendError = '';
      this.signupPhoneE164 = '';
      this.signupPhoneMasked = '';
      if (this.socialEmail) {
        this.socialEmail = '';
        this.pendingSignupToken = '';
        this.signupProfileForm.reset();
        this.updateSignupProfileContinue();
        this.step = 'email';
      } else {
        this.step = 'otpVerify';
        this.pendingSignupToken = '';
      }
      return;
    }
    if (this.step === 'facebookEmailCollection') {
      this.facebookBindingToken = '';
      this.facebookEmailCollected = '';
      this.facebookEmailBindingFlow = false;
      this.emailForm.reset();
      this.errorMessage = '';
      this.step = 'email';
      return;
    }
    if (this.step === 'socialChallenge') {
      this.clearCiamResendCooldown();
      this.pendingSocialChallengeToken = '';
      this.socialChallengeEmail = '';
      this.socialChallengeMaskedEmail = '';
      this.facebookBindingToken = '';
      this.facebookEmailBindingFlow = false;
      this.otpForm.reset();
      this.errorMessage = '';
      this.signupProfilePhoneSendError = '';
      this.otpRateLimitBanner = '';
      this.step = 'email';
      return;
    }
    this.clearCiamResendCooldown();
    this.step = 'email';
    this.errorMessage = '';
    this.signupProfilePhoneSendError = '';
    this.otpRateLimitBanner = '';
    this.pendingSignupToken = '';
    this.otpForm.reset();
    this.passwordForm.reset();
    this.signupProfileForm.reset();
    this.updateSignupProfileContinue();
  }

  onResendOtp(): void {
    if (this.loading || this.otpResendDisabled || (this.ciamV6Com && this.resendCooldownSeconds > 0)) {
      return;
    }
    this.onOtpSendCode();
  }

  private onCiamOtpSent(): void {
    if (!this.ciamV6Com) {
      return;
    }
    this.startCiamResendCooldown();
  }

  private startCiamResendCooldown(): void {
    this.clearResendTimer();
    this.resendCooldownSeconds = RESEND_COOLDOWN_SECONDS;
    this.resendTimer = setInterval(() => {
      this.resendCooldownSeconds -= 1;
      if (this.resendCooldownSeconds <= 0) {
        this.clearResendTimer();
        this.resendCooldownSeconds = 0;
      }
      this.cdr.markForCheck();
    }, 1000);
    this.cdr.markForCheck();
  }

  private clearCiamResendCooldown(): void {
    this.clearResendTimer();
    this.resendCooldownSeconds = 0;
  }

  private clearResendTimer(): void {
    if (this.resendTimer != null) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
  }

  showSignupModal(): void {
    this.showModal(this.modalService.getSignupModalSelector(), false);
  }

  private setFacebookChallengeBinding(facebookBindingToken?: string | null): void {
    const token = (facebookBindingToken ?? '').trim();
    this.facebookBindingToken = token;
    this.facebookEmailBindingFlow = token.length > 0;
  }

  /** Backend should substitute TT email for @noemail.wb.invalid; if a bad challenge still arrives, collect email. */
  private shouldCollectFacebookEmailInsteadOfChallenge(maskedEmail?: string | null, resolvedEmail?: string | null): boolean {
    if (!this.facebookBindingToken?.trim()) {
      return false;
    }
    const resolved = (resolvedEmail ?? '').trim().toLowerCase();
    const masked = (maskedEmail ?? '').trim().toLowerCase();
    if (!resolved) {
      return true;
    }
    if (resolved.endsWith('@noemail.wb.invalid') || masked.endsWith('@noemail.wb.invalid')) {
      return true;
    }
    return false;
  }

  private resolveSocialChallengeEmail(maskedFallback: string, challengeEmail?: string | null): string {
    const fromChallenge = (challengeEmail ?? '').trim();
    if (fromChallenge && !fromChallenge.includes('*') && fromChallenge.includes('@')) {
      return fromChallenge;
    }
    const fromExternal = this.readExternalSignupEmail();
    const candidates = [
      this.facebookEmailCollected,
      fromExternal,
      this.authService.facebookProfile?.email,
      this.socialEmail,
      this.emailValue,
    ];
    for (const c of candidates) {
      const v = (c ?? '').toString().trim();
      if (v && !v.includes('*') && v.includes('@') && !v.toLowerCase().endsWith('@noemail.wb.invalid')) {
        return v;
      }
    }
    //Never use maskedFallback as an OTP identifier (Auth0 rejects m****@...).
    return '';
  }

  private readExternalSignupEmail(): string {
    const request = this.authService.getExternalRequest?.();
    const params = request?.externalParams;
    if (!Array.isArray(params)) {
      return '';
    }
    const emailParam = params.find((p: { name?: string; value?: unknown }) => p?.name === 'email');
    return (emailParam?.value ?? '').toString().trim();
  }

  showModal(name: string, autoHide = true): void {
    setTimeout(() => {
      if (this.returnUrl) {
        this.router.navigate(['/home'], {
          queryParams: { [Constants.RETURN_URL]: this.returnUrl },
        });
      }
    }, 500);
    setTimeout(() => {
      this.modalService.setModalSelector(name);
      this.modalService.showModal(autoHide);
    }, 200);
  }

  getProviderLabel(provider: string): string {
    return PROVIDER_LABELS[provider?.toLowerCase()] ?? provider;
  }

  hasProvider(name: string): boolean {
    return this.socialProviders.some((p) => (p ?? '').toLowerCase().includes(name.toLowerCase()));
  }
}
